import React from "react";
import {Navigate, useParams} from "react-router-dom";
import {UserPermissionEnum} from "../../utils/types/user";

import {
  ReportingBlock,
  ReportingBlockSetters,
} from "../../atoms/reporting-block";
import {DocumentTitle} from "../../atoms/document-title";
import {RequiredUserPermissionsSome} from "../../atoms/admin-user-permissions/RequiredUserPermissionsSome";
import {ReportingGroupRenderer} from "../../atoms/admin-reporting/atoms/reporting-group/ReportingGroup";
import {ReportingLayout} from "./reporting-layout";
import {ReportChecklistItem} from "./reports";
import {ReportContextConsumer} from "./ReportContext";
import {UseChecklistItems} from "./reporting-menu/useChecklistItems";
import {Banner} from "../../atoms/banner";
import {reportingListPath} from "./reporting-paths";
import {ReportingListUrlSync} from "./ReportingListUrlSync";

export const PageReportingReportsCheckboxes = () => {
  const {sectionId, from1, to1} = useParams<{
    sectionId?: string;
    from1?: string;
    to1?: string;
  }>();

  return (
    <ReportingLayout>
      <ReportingListUrlSync from1={from1} to1={to1} />
      <DocumentTitle title="Reside - Admin - Reporting | Checkboxes" />
      <RequiredUserPermissionsSome
        permissions={[UserPermissionEnum.CHECKLIST_ITEMS]}
        noPermissionChildren={
          <Banner title="No reports are available for this section.">
            <p>Please choose a different report from the left menu.</p>
          </Banner>
        }
      >
        <ReportContextConsumer>
          {({baseDateRange, activeFacilitiesIds, setPageLoading}) => (
            <UseChecklistItems>
              {({data: checklistItems = [], isFetched}) => {
                if (!isFetched) {
                  return null;
                }

                if (checklistItems.length === 0) {
                  return (
                    <Banner title="No reports are available for this section.">
                      <p>Please choose a different report from the left menu.</p>
                    </Banner>
                  );
                }

                const defaultPath = reportingListPath(
                  "checkboxes",
                  checklistItems[0].id,
                );

                if (!sectionId) {
                  return <Navigate to={defaultPath} replace />;
                }

                const item = checklistItems.find(({id}) => id === sectionId);

                if (!item) {
                  return <Navigate to={defaultPath} replace />;
                }

                const {id, description, workflowType} = item;

                return (
                  <ReportingGroupRenderer key={id} id={id} title={description}>
                    <ReportingBlock
                      key={id}
                      chartId={id}
                      permissionInChart={[UserPermissionEnum.CHECKLIST_ITEMS]}
                      dateRange={baseDateRange}
                      facilities={activeFacilitiesIds}
                      setPageLoading={setPageLoading}
                      isDynamicCheckbox
                      hideCardExport
                    >
                      {(reportingBlockSetters: ReportingBlockSetters) => (
                        <ReportChecklistItem
                          id={id}
                          description={description}
                          workflowType={workflowType}
                          dateRange={baseDateRange}
                          facilities={activeFacilitiesIds}
                          reportingBlockSetters={reportingBlockSetters}
                        />
                      )}
                    </ReportingBlock>
                  </ReportingGroupRenderer>
                );
              }}
            </UseChecklistItems>
          )}
        </ReportContextConsumer>
      </RequiredUserPermissionsSome>
    </ReportingLayout>
  );
};
